// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — RBAC Type Definitions
// ═══════════════════════════════════════════════════════════════

export type PermissionLevel = 'none' | 'viewer' | 'editor' | 'admin';

export type ModuleId =
  | 'hotwash' | 'handoff' | 'rollcall' | 'geoops'
  | 'impact' | 'intake' | 'battlerhythm' | 'pace'
  | 'volunteer' | 'mainstreet' | 'filelibrary' | 'blackboard' | 'incidents'
  | 'debris' | 'shelter' | 'pagrants' | 'timelabor' | 'workflows' | 'forms';

export const ALL_MODULE_IDS: ModuleId[] = [
  'hotwash', 'handoff', 'rollcall', 'geoops',
  'impact', 'intake', 'battlerhythm', 'pace',
  'volunteer', 'mainstreet', 'filelibrary', 'blackboard', 'incidents',
  'debris', 'shelter', 'pagrants', 'timelabor', 'workflows', 'forms',
];

export type RbacPermissions = Record<ModuleId, PermissionLevel>;

export interface Role {
  id: number;
  Rid: number;
  roleName: string;
  roleSlug: string;
  description: string;
  permissions: RbacPermissions;
  isDefault: boolean;
  sortOrder: number;
}

export interface UserRole {
  id: number;
  Rid: number;
  userEmail: string;
  userName: string;
  roleId: string;
  roleName: string;
  assignedBy: string;
}

export interface ModuleAccess {
  level: PermissionLevel;
  canView: boolean;
  canEdit: boolean;
  canAdmin: boolean;
}

/** Permission level hierarchy for comparison */
const LEVEL_ORDER: Record<PermissionLevel, number> = {
  none: 0,
  viewer: 1,
  editor: 2,
  admin: 3,
};

export function hasMinLevel(actual: PermissionLevel, required: PermissionLevel): boolean {
  return LEVEL_ORDER[actual] >= LEVEL_ORDER[required];
}

export function toModuleAccess(level: PermissionLevel): ModuleAccess {
  return {
    level,
    canView: level !== 'none',
    canEdit: level === 'editor' || level === 'admin',
    canAdmin: level === 'admin',
  };
}

export const FULL_ACCESS: ModuleAccess = { level: 'admin', canView: true, canEdit: true, canAdmin: true };
export const NO_ACCESS: ModuleAccess = { level: 'none', canView: false, canEdit: false, canAdmin: false };

/** Default permissions (all none) */
export function emptyPermissions(): RbacPermissions {
  const p = {} as RbacPermissions;
  ALL_MODULE_IDS.forEach(m => { p[m] = 'none'; });
  return p;
}
